import { useState } from 'react';
import { Copy, Check, Users, DollarSign, Share2, Gift, Bell, ArrowLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useAppState } from '../context/AppStateContext';
import Sidebar from '../components/layout/Sidebar';
import BottomNav from '../components/layout/BottomNav';
import GlobalHeader from '../components/layout/GlobalHeader';
import NotificationsPanel from '../components/shared/NotificationsPanel';

// Mock referral data
const REFERRAL_CODE = 'BONNDLE-JAM3S';
const REFERRAL_LINK = 'https://bonndle.app/join/BONNDLE-JAM3S';
const REFERRAL_STATS = {
  totalEarned: '$245.00',
  totalReferrals: 12,
  rewardPerReferral: '$10.00',
};

const REFERRAL_HISTORY = [
  { id: 1, name: 'Sarah M.', avatar: 'Sarah', joinDate: 'Mar 28, 2026', status: 'active', reward: '$10.00' },
  { id: 2, name: 'Jordan P.', avatar: 'Jordan', joinDate: 'Mar 25, 2026', status: 'active', reward: '$10.00' },
  { id: 3, name: 'Kim L.', avatar: 'Kim', joinDate: 'Mar 22, 2026', status: 'active', reward: '$10.00' },
  { id: 4, name: 'Alex R.', avatar: 'Alex', joinDate: 'Mar 20, 2026', status: 'active', reward: '$10.00' },
  { id: 5, name: 'Taylor W.', avatar: 'Taylor', joinDate: 'Mar 18, 2026', status: 'active', reward: '$10.00' },
  { id: 6, name: 'Chris B.', avatar: 'Chris', joinDate: 'Mar 15, 2026', status: 'pending', reward: '$10.00' },
  { id: 7, name: 'Morgan T.', avatar: 'Morgan', joinDate: 'Mar 12, 2026', status: 'active', reward: '$10.00' },
  { id: 8, name: 'Casey D.', avatar: 'Casey', joinDate: 'Mar 10, 2026', status: 'active', reward: '$10.00' },
  { id: 9, name: 'Riley S.', avatar: 'Riley', joinDate: 'Mar 8, 2026', status: 'active', reward: '$10.00' },
  { id: 10, name: 'Quinn H.', avatar: 'Quinn', joinDate: 'Mar 5, 2026', status: 'active', reward: '$10.00' },
  { id: 11, name: 'Avery N.', avatar: 'Avery', joinDate: 'Mar 2, 2026', status: 'active', reward: '$10.00' },
  { id: 12, name: 'Jamie K.', avatar: 'Jamie', joinDate: 'Feb 28, 2026', status: 'active', reward: '$10.00' },
];

export default function ReferralsPage() {
  const { isDesktop } = useResponsive();
  const { showNotifications, setShowNotifications } = useAppState();

  if (isDesktop) {
    return (
      <div className="flex h-screen h-dvh bg-gray-50">
        <Sidebar />
        <main className="flex-1 flex flex-col overflow-hidden ml-64">
          <GlobalHeader />
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            <ReferralsContent />
          </div>
        </main>

        {showNotifications && (
          <NotificationsPanel onClose={() => setShowNotifications(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen h-dvh bg-gray-50">
      <main className="flex-1 flex flex-col overflow-hidden pb-nav">
        <ReferralsContent />
      </main>
      <BottomNav />

      {showNotifications && (
        <NotificationsPanel onClose={() => setShowNotifications(false)} />
      )}
    </div>
  );
}

function ReferralsContent() {
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();
  const { setShowNotifications, unreadCount } = useAppState();
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(REFERRAL_CODE);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(REFERRAL_LINK);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Bonndle',
          text: `Join me on Bonndle and get $10 in rewards! Use my referral code: ${REFERRAL_CODE}`,
          url: REFERRAL_LINK,
        });
      } catch {
        // User cancelled or share failed
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="flex-1 overflow-y-auto scrollbar-hide">
      {/* Mobile Header */}
      {!isDesktop && (
        <header className="sticky top-0 bg-white z-10 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/menu')}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Referrals</h1>
          </div>
          <button
            onClick={() => setShowNotifications(true)}
            className="relative p-2 text-gray-500 hover:text-gray-700"
          >
            <Bell size={24} />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        </header>
      )}

      <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-6 mb-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Gift size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Refer & Earn</h2>
              <p className="text-sm text-white/80">Earn {REFERRAL_STATS.rewardPerReferral} for every friend who joins</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={16} className="text-emerald-300" />
                <span className="text-xs font-medium text-white/70">Total Earned</span>
              </div>
              <p className="text-2xl font-bold">{REFERRAL_STATS.totalEarned}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users size={16} className="text-blue-300" />
                <span className="text-xs font-medium text-white/70">Total Referrals</span>
              </div>
              <p className="text-2xl font-bold">{REFERRAL_STATS.totalReferrals}</p>
            </div>
          </div>
        </div>

        {/* Referral Code Section */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Your Referral Code</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-xl px-4 py-3 flex items-center justify-between">
              <code className="text-lg font-mono font-bold text-gray-900 tracking-wider">{REFERRAL_CODE}</code>
              <button
                onClick={handleCopyCode}
                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
              >
                {copiedCode ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} />}
              </button>
            </div>
          </div>
          {copiedCode && <p className="text-xs text-emerald-600 font-medium mt-2">Code copied to clipboard!</p>}
        </div>

        {/* Referral Link Section */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Your Referral Link</p>
          <div className="bg-gray-100 rounded-xl px-4 py-3 mb-3">
            <p className="text-sm font-medium text-gray-700 truncate">{REFERRAL_LINK}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCopyLink}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold text-sm hover:bg-gray-200 active:scale-[0.98] transition-all"
            >
              {copiedLink ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} />}
              {copiedLink ? 'Copied!' : 'Copy Link'}
            </button>
            <button
              onClick={handleShare}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-xl font-bold text-sm hover:bg-indigo-700 active:scale-[0.98] transition-all"
            >
              <Share2 size={18} />
              Share
            </button>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">How It Works</p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-indigo-600">1</span>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-900">Share your code or link</p>
                <p className="text-xs text-gray-500">Send it to friends via text, email, or social media</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-indigo-600">2</span>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-900">Friend signs up</p>
                <p className="text-xs text-gray-500">They use your code during registration</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-emerald-600">3</span>
              </div>
              <div>
                <p className="font-bold text-sm text-gray-900">You both earn rewards!</p>
                <p className="text-xs text-gray-500">Get {REFERRAL_STATS.rewardPerReferral} when they make their first purchase</p>
              </div>
            </div>
          </div>
        </div>

        {/* Referral History */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Referral History</p>
              <span className="text-xs font-bold text-gray-500">{REFERRAL_HISTORY.length} referrals</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {REFERRAL_HISTORY.map((referral) => (
              <div key={referral.id} className="flex items-center gap-3 p-4">
                <img
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${referral.avatar}`}
                  alt={referral.name}
                  className="w-10 h-10 rounded-full bg-gray-100"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-gray-900">{referral.name}</p>
                  <p className="text-xs text-gray-500">Joined {referral.joinDate}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm text-emerald-600">{referral.reward}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    referral.status === 'active'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {referral.status === 'active' ? 'Earned' : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
